import { Show, createResource, useContext } from 'solid-js';
import { gql } from 'graphql-request';
import { FormsTw } from '../forms-tw/forms';
import { FieldType, FormStep } from '../forms-tw/forms-types';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';

/**
 * Props passed from the WordPress global-form-selector block
 * via the headless_helper filter
 */
interface FieldOverride {
    placeholder?: string;
    options?: Record<string, string>;
}

export interface GlobalFormSelectorProps {
    globalForm: {
        templateSlug?: string;
        templateUuid?: string;
        hideStepNames?: boolean;
        formCampaignId?: string;
        successMessageTitle?: string;
        successMessageText?: string;
        fieldLabelOverrides?: Record<string, FieldOverride>;
        __missingRef?: boolean;
    };
}

/**
 * GraphQL response structure for getFormTemplateBySlug query
 */
interface FormTemplateResponse {
    getFormTemplateBySlug: {
        uuid: string;
        name: string;
        slug: string;
        description?: string;
        steps: FormStep[];
    } | null;
}

const GET_FORM_TEMPLATE_BY_SLUG = gql`
    query GetFormTemplateBySlug($slug: String!) {
        getFormTemplateBySlug(slug: $slug) {
            uuid
            name
            slug
            description
            steps {
                id
                stepName
                fields {
                    id
                    slug
                    name
                    required
                    requiredInForm
                    instance
                    extraSettings {
                        placeholder {
                            value
                        }
                        name {
                            value
                        }
                        options {
                            value
                        }
                        fieldValue {
                            value
                        }
                        translation {
                            value
                        }
                        info {
                            value
                        }
                        link {
                            value
                        }
                        linkText {
                            value
                        }
                        hcpOnly {
                            value
                        }
                        patientOnly {
                            value
                        }
                    }
                }
            }
        }
    }
`;

/**
 * GlobalFormSelector component
 *
 * This component renders global form templates that are centrally managed
 * in the Network Admin via the global-forms microservice.
 *
 * Unlike regular FormSelector which receives form data directly from WordPress,
 * this component fetches the form template by slug from the GraphQL API.
 *
 * Consent settings (single/double checkbox) are fetched by the FormsTw component
 * based on the user's selected country in the form.
 */
export const GlobalFormSelector = (props: GlobalFormSelectorProps) => {
    const AppState = useContext(AppContext);
    const { graphQlClient, localize } = AppState;

    // Show nothing if the block has a missing reference
    if (props.globalForm?.__missingRef) {
        return null;
    }

    const templateSlug = props.globalForm?.templateSlug;

    // If no slug provided, show nothing
    if (!templateSlug) {
        return null;
    }

    // Fetch the form template by slug
    const [formTemplate] = createResource(
        () => templateSlug,
        async (slug) => {
            try {
                const response = await graphQlClient(
                    GET_FORM_TEMPLATE_BY_SLUG,
                    { slug }
                ) as FormTemplateResponse;
                if (!response) {
                    return null;
                }
                return response.getFormTemplateBySlug;
            } catch (error) {
                console.error('[GlobalFormSelector] Error fetching global form template:', error);
                return null;
            }
        }
    );

    /**
     * Transform the fetched form template to match the FormProps structure
     * expected by the FormsTw component.
     *
     * Applies per-market field label overrides from the Gutenberg block attributes.
     * Overrides are keyed by the field's extraSettings.name.value (its submission name)
     * and replace the placeholder.value used as the display label.
     */
    const getFormData = () => {
        const template = formTemplate();
        if (!template) return null;

        const overrides = props.globalForm?.fieldLabelOverrides;

        // Filter out 'acceptPolicy' fields from each step
        // (the form component handles policy acceptance separately)
        const stepsWithoutAcceptPolicy = template.steps.map((step) => ({
            ...step,
            fields: step.fields
                .filter((field) => field.slug !== 'acceptPolicy')
                .map((field): FieldType => {
                    // Apply per-market label and options overrides to custom fields
                    const fieldKey = field.extraSettings?.name?.value;
                    const override = overrides && fieldKey ? overrides[fieldKey] : undefined;
                    if (!override || !field.extraSettings) return field;

                    // Rebuild the options string with translated labels.
                    // Original format: "value|label\n..." — replace labels where overrides exist.
                    let optionsOverride: { value: string } | undefined;
                    if (override.options && field.extraSettings.options?.value) {
                        const translatedLines = field.extraSettings.options.value
                            .split('\n')
                            .map((line) => {
                                const trimmed = line.trim();
                                if (!trimmed) return trimmed;
                                const pipeIndex = trimmed.indexOf('|');
                                const value = pipeIndex >= 0 ? trimmed.substring(0, pipeIndex).trim() : trimmed;
                                const translatedLabel = override.options![value];
                                if (translatedLabel) {
                                    return `${value}|${translatedLabel}`;
                                }
                                return trimmed;
                            })
                            .join('\n');
                        optionsOverride = { value: translatedLines };
                    }

                    return {
                        ...field,
                        extraSettings: {
                            ...field.extraSettings,
                            ...(override.placeholder ? { placeholder: { value: override.placeholder } } : {}),
                            ...(optionsOverride ? { options: optionsOverride } : {}),
                        },
                    };
                }),
        }));

        // Remove empty steps
        const nonEmptySteps = stepsWithoutAcceptPolicy.filter(
            (step) => step.fields && step.fields.length > 0
        );

        return {
            steps: nonEmptySteps,
            hideStepNames: props.globalForm?.hideStepNames ?? false,
            formCampaignId: props.globalForm?.formCampaignId || undefined,
            successMessageTitle: props.globalForm?.successMessageTitle || undefined,
            successMessageText: props.globalForm?.successMessageText || undefined,
        };
    };

    return (
        <Show
            when={!formTemplate.loading}
            fallback={
                <div class="flex justify-center items-center p-cvi-9">
                    <LoadingSpinner />
                </div>
            }
        >
            <Show
                when={formTemplate() && getFormData()}
                fallback={
                    <div class="label-md font-cvi-regular text-cvi-fg-muted text-center p-cvi-6">
                        {localize('form-not-found', 'Form not found')}
                    </div>
                }
            >
                {(data) => <FormsTw {...data()} />}
            </Show>
        </Show>
    );
};
